import { FC, Suspense } from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import { BLOCKS } from '@contentful/rich-text-types';
import cn from 'classnames';

import type { Store } from 'app-redux/types/storeTypes';
import type { Block } from 'src/__generated__/graphqlTypes';
import { CommonRichText as Content } from 'components/Shared/SharedComponents';
import type { RichTextParsersConfig } from 'types/objectTypes';
import { mapBlocksSpan } from 'lib/richTextMappers';

import styles from './ContentWithSubBlocks.module.scss';

import type { ISliderView, ITitle } from '../config/declarations';

const MobileCarousel = dynamic(() => import('./MobileCarousel'));

const parsersConfig: RichTextParsersConfig = {
  [BLOCKS.HEADING_1]: {
    mapper: mapBlocksSpan,
    classNames: cn(styles.title, styles.titleH1),
  },
  [BLOCKS.HEADING_2]: {
    mapper: mapBlocksSpan,
    classNames: cn(styles.title, styles.titleH2),
  },
  [BLOCKS.HEADING_3]: {
    mapper: mapBlocksSpan,
    classNames: cn(styles.title, styles.titleH3),
  },
  [BLOCKS.PARAGRAPH]: {
    classNames: styles.paragraph,
  },
};

export const SliderView: FC<ISliderView> = ({ block }) => {
  const isMobileViewWidth = useSelector((store: Store) => store.server.app.isMobileViewWidth);

  const childrenBlocks = block.contentTypesCollection?.items as Array<Block>;

  if (!childrenBlocks?.length) {
    return null;
  }

  const children = childrenBlocks.map((item) => {
    if (isMobileViewWidth && item.slug === 'divider') {
      return null;
    }

    if (item.slug === 'divider') {
      return <div key={item.entryName} className={styles.divider} />;
    }

    return (
      <div key={item.entryName} className={styles.statistics}>
        <Content content={item.content} parsersConfig={parsersConfig} />
      </div>
    );
  });

  if (isMobileViewWidth) {
    return (
      <Suspense fallback={children}>
        <MobileCarousel>{children.filter((item) => Boolean(item))}</MobileCarousel>
      </Suspense>
    );
  }

  return (
    <>
      {children}
    </>
  );
};

export const SliderTitle: FC<ITitle> = ({ content }) => (
  <Content
    content={content}
    parsersConfig={parsersConfig}
  />
);
